import { boolean, number, object, string } from 'yup';
import { useMemo } from 'react';
import {
  toRepliconDate,
  dateToMidnightUTCString
} from '~/modules/common/dates/convert';
import { getResourceRequestDatesFromProjectDate } from '~/modules/resourcing/common/util';
import { roundToDecimals } from '~/modules/resourcing/common/util/scheduleUtil';
import { getMaxDateForAllocation } from '~/modules/resourcing/components/AllocateResourcePopup/hooks/useAllocateResourcePopupHandler';
import { useGetScheduleHoursForUserAndDateRange } from '~/modules/resourcing/common/hooks/useGetScheduleHoursForUserAndDateRange';
import { ALLOCATION_PERIOD } from '../../AllocationByPeriod';

const validationSchema = object().shape({
  startDate: object()
    .shape({
      year: number().required(),
      month: number().required(),
      day: number().required()
    })
    .required(),
  endDate: object()
    .shape({
      year: number().required(),
      month: number().required(),
      day: number().required()
    })
    .required(),
  isClearAllocationChecked: boolean(),
  selectedAllocationPeriod: string()
    .oneOf(Object.values(ALLOCATION_PERIOD))
    .required()
});

export const useSetResourceAllocationFormState = ({
  project,
  scale,
  userUri
} = {}) => {
  const {
    requestStartDate: defaultStartDate,
    requestEndDate: defaultEndDate
  } = getResourceRequestDatesFromProjectDate({
    projectStartDate: project.startDate,
    projectEndDate: project.endDate,
    scale
  });

  const maxEndDate = getMaxDateForAllocation({
    startDate: defaultStartDate,
    endDate: defaultEndDate
  });

  const {
    scheduledHours,
    loading: isScheduleLoading
  } = useGetScheduleHoursForUserAndDateRange({
    userUri,
    dateRange: {
      startDate: dateToMidnightUTCString(defaultStartDate),
      endDate: dateToMidnightUTCString(maxEndDate)
    },
    skip: !userUri
  });

  const totalScheduledHours = scheduledHours || 0;

  const initialValues = useMemo(
    () => ({
      startDate: toRepliconDate(defaultStartDate),
      endDate: toRepliconDate(maxEndDate),
      isClearAllocationChecked: false,
      selectedAllocationPeriod: ALLOCATION_PERIOD.ENTIRE_PERIOD,
      hoursForEntirePeriod:
        totalScheduledHours > 0 ? roundToDecimals(totalScheduledHours) : 0,
      percentageForEntirePeriod: 100,
      hoursPerMonth: '',
      hoursPerWeek: ''
    }),
    [defaultStartDate, maxEndDate, totalScheduledHours]
  );

  return {
    initialValues,
    validationSchema,
    totalScheduledHours,
    isScheduleLoading
  };
};
